import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    InterestBreadcrumbs,
    InterestCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as InterestActions from "../../../actions/interests";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { LookupsLayout } from "../../index";

import "./InterestPage.scss";

interface InterestPageProps {
    form: Dtos.Interest & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    interest: Dtos.Interest;
    lookups: Dtos.Lookup[];
    loadingInterest: boolean;
    loadInterestFailure: boolean;
    loadInterestErrorCode: string;
    savingInterest: boolean;
    saveInterestSuccess: boolean;
    saveInterestFailure: boolean;
    creatingInterest: boolean;
    createInterestSuccess: boolean;
    validatingInterest: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearInterest: InterestActions.IInterestClearActionCreator;
    loadInterest: InterestActions.IInterestLoadByIdActionCreator;
    saveInterest: InterestActions.IInterestSaveActionCreator;
    createInterest: InterestActions.IInterestCreateActionCreator;
    validateInterest: InterestActions.IInterestValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.interest";

const InterestPage: FC<InterestPageProps> = ({
    form,
    formState,
    formProperties,
    interest,
    lookups,
    loadingInterest,
    loadInterestFailure,
    loadInterestErrorCode,
    savingInterest,
    saveInterestSuccess,
    saveInterestFailure,
    creatingInterest,
    createInterestSuccess,
    validatingInterest,
    validationFailures,
    permissions,
    clearInterest,
    loadInterest,
    saveInterest,
    createInterest,
    validateInterest,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { interestId } = useParams<{ interestId: string }>();
    const interestIdNumber = parseInt(interestId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.Interest = {} as Dtos.Interest;

        loadForm(reduxFormName, Object.assign({ ...interest }, extraData));
    };


    // Load country
    useEffect(() => {
        if (interestIdNumber && !interest && !loadingInterest) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(InterestBreadcrumbs(interestIdNumber));
            loadInterest(interestIdNumber);
        } else if (
            !interestIdNumber &&
            !creatingInterest &&
            !createInterestSuccess
        ) {
            createInterest();
            setBreadcrumbs(InterestCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (interest && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        interestIdNumber,
        interest,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        InterestBreadcrumbs,
        loadInterest,
        createInterest,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingInterest,
        creatingInterest,
        createInterestSuccess,
        InterestCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInterestFailure) {
            navigate(`/error/${loadInterestErrorCode}`);
        }
    }, [loadInterestFailure, loadInterestErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (interestIdNumber && interest?.value) {
            setBreadcrumbs(
                InterestBreadcrumbs(interestIdNumber, interest?.value)
            );
        }
    }, [interestIdNumber, interest, setBreadcrumbs, InterestBreadcrumbs]);

    // Reload interest after saving
    useEffect(() => {
        if (!savingInterest && saveInterestSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Interest saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/general/interests";
                navigate(url);
            } else if (!interestIdNumber) {
                let url: string = `/settings/general/interests/${interest?.id}`;
                navigate(url);
                clearInterest();
            }
        }
    }, [
        savingInterest,
        saveInterestSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingInterest && saveInterestFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingInterest, saveInterestFailure]);

    // Cleanup effect to clear the interest on component unmount
    useEffect(() => {
        return () => {
            clearInterest();
            resetForm(reduxFormName);
        };
    }, [clearInterest, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/general/interests";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.Interest) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.Interest = {} as Dtos.Interest;

            await saveInterest(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.Interest = {} as Dtos.Interest;

            if (form && hasBeenSubmitted && !validatingInterest) {
                await validateInterest(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.InterestsUpdate,
                Dtos.Permission.InterestsCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingInterest ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "interest",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "interest",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "interest",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "interest",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingInterest,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <LookupsLayout
            loading={loadingInterest}
            permission={Dtos.Permission.InterestsView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<InterestPageProps> => {
    let interest: Dtos.Interest | undefined = !(
        state.interests.formData instanceof Array
    )
        ? state.interests.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        interest: interest!,
        loadingInterest:
            state.interests.loadState &&
            state.interests.loadState.status == RequestState.Pending,
        loadInterestFailure:
            state.interests.loadState &&
            state.interests.loadState.status == RequestState.Failure,
        loadInterestErrorCode: state.interests.loadState.errorCode,
        savingInterest:
            state.interests.saveState &&
            state.interests.saveState.status == RequestState.Pending,
        saveInterestSuccess:
            state.interests.saveState &&
            state.interests.saveState.status == RequestState.Success,
        saveInterestFailure:
            state.interests.saveState &&
            state.interests.saveState.status == RequestState.Failure,
        creatingInterest:
            state.interests.createState &&
            state.interests.createState.status == RequestState.Pending,
        createInterestSuccess:
            state.interests.createState &&
            state.interests.createState.status == RequestState.Success,
        validatingInterest:
            state.interests.validateState &&
            state.interests.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.interest,
        formState: state.interests.formState,
        formProperties: state.interests.formProperties,
        validationFailures: interest
            ? state.interests.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<InterestPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadInterest: bindActionCreators(
            InterestActions.LoadInterestById,
            dispatch
        ),
        clearInterest: bindActionCreators(InterestActions.Clear, dispatch),
        saveInterest: bindActionCreators(
            InterestActions.SaveInterest,
            dispatch
        ),
        createInterest: bindActionCreators(
            InterestActions.CreateInterest,
            dispatch
        ),
        validateInterest: bindActionCreators(
            InterestActions.ValidateInterest,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(InterestPage);
